import dayjs from 'dayjs';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import minMax from 'dayjs/plugin/minMax';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import 'dayjs/locale/th';
import type { Optional } from '../types';

dayjs.extend(buddhistEra);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(minMax);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('th');

const DEFAULT_TIMEZONE = 'Asia/Bangkok';

/**
 * Format date to DD-MM-YYYY in Asia/Bangkok time
 *
 * @param date Date to be formatted
 * @returns Date in format DD-MM-YYYY
 */
function formatDate(date: Optional<Date | number>) {
  if (!date) {
    return '-';
  }
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.tz(DEFAULT_TIMEZONE).format('DD-MM-YYYY') : '-';
}

/**
 * Format date to DD-MM-BBBB (Buddhist Era) in Asia/Bangkok time
 *
 * @param date Date to be formatted
 * @returns Date in format DD-MM-BBBB, where BBBB represents Buddhist Era years
 */
function formatDateTh(date: Optional<Date | number>) {
  if (!date) {
    return '-';
  }
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.tz(DEFAULT_TIMEZONE).format('DD-MM-BBBB') : '-';
}

/**
 * Format date to DD-MM-YYYY, HH:mm in Asia/Bangkok time
 *
 * @param date Date to be formatted
 * @returns Date in format DD-MM-YYYY, HH:mm
 */
function formatDateTime(date: Optional<Date | number>) {
  if (!date) {
    return '-';
  }
  const dayjsDate = dayjs(date);
  return dayjsDate.isValid() ? dayjsDate.tz(DEFAULT_TIMEZONE).format('DD-MM-YYYY, HH:mm') : '-';
}

function formatTimeToThaiUnits({ hours, minutes }: { hours: number; minutes: number }) {
  const hoursForCalculation = hours >= 0 ? hours : 0;
  const minutesForCalculation = minutes >= 0 ? minutes : 0;

  const totalMinutes = hoursForCalculation * 60 + minutesForCalculation;

  const displayTime = dayjs.duration(totalMinutes, 'minutes');
  const calculatedHours = displayTime.asHours();
  const calculatedMinutes = displayTime.minutes();

  const displayHours = calculatedHours >= 1 ? `${Math.floor(calculatedHours)} ชั่วโมง` : undefined;
  const displayMinutes = calculatedMinutes > 0 ? `${calculatedMinutes} นาที` : undefined;

  if (!displayHours && !displayMinutes) {
    return '0 นาที';
  }

  return [displayHours, displayMinutes].filter(Boolean).join(' ');
}

export { DEFAULT_TIMEZONE, dayjs, formatDate, formatDateTh, formatDateTime, formatTimeToThaiUnits };
